/* eslint no-useless-escape: 0 */

import React, { useState } from 'react';
import { Formik, Field, ErrorMessage } from 'formik';
import styles from './style.module.css';
import exemptionList from 'helpers/exemptions';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { validateNationalInsuranceNumber } from 'helpers/yupmatchers';
import { useDispatch } from 'react-redux';
import { ADD_EXEMPTION } from 'helpers/types';
import moment from 'moment';
import { useExemption, useFeatureFlagCheck } from 'hooks';

const ExemptionFormSchema = Yup.object().shape({
  payForPrescriptions: Yup.string(),
  exemption: Yup.string().when('payForPrescriptions', {
    is: (payForPrescriptions) => {
      if (payForPrescriptions === 'Yes') return false;
      return true;
    },
    then: Yup.string().required('Please select an exemption'),
  }),
  ni_number: Yup.string().when(['payForPrescriptions', 'exemption'], {
    is: (payForPrescriptions, exemption) => {
      if (payForPrescriptions === 'Yes') return false;
      return !!exemptionList.find((reason) => reason.option === exemption && reason?.validation?.type === 'ni');
    },
    then: validateNationalInsuranceNumber,
  }),
  cert: Yup.object().when(['payForPrescriptions', 'exemption'], {
    is: (payForPrescriptions, exemption) => {
      if (payForPrescriptions === 'Yes') return false;
      return !!exemptionList.find(
        (reason) => reason.option === exemption && reason?.validation?.type === 'cert_expiry'
      );
    },
    then: Yup.object().shape({
      number: Yup.string()
        .label('Certificate number')
        .test({
          name: 'is-proper-length',
          test: function(value) {
            const {options: {context : {exemption}}} = this;
            const selectedExemption = exemptionList.find((reason) => reason.option === exemption);
            const length = selectedExemption.validation?.cert_number?.length || null;

            if (!length) return true
            if (value.length !== length) return this.createError({message: `Certificate number must be ${length} characters`});
            return true
          }
        }),
      expiry: Yup.string()
        .test('is-date-format', 'Must have date format dd-mm-yyyy', (value) => {
          if (value === undefined) return true;
          return (
            /(^(((0[1-9]|1[0-9]|2[0-8])[\-](0[1-9]|1[012]))|((29|30|31)[\-](0[13578]|1[02]))|((29|30)[\-](0[4,6,9]|11)))[\-](19|[2-9][0-9])\d\d$)|(^29[\-]02[\-](19|[2-9][0-9])(00|04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)$)/.test(
              value
            ) ||
            /^(?:\d{4}\-(?:(?:(?:(?:0[13578]|1[02])\-(?:0[1-9]|[1-2][0-9]|3[01]))|(?:(?:0[469]|11)\-(?:0[1-9]|[1-2][0-9]|30))|(?:02\-(?:0[1-9]|1[0-9]|2[0-8]))))|(?:(?:\d{2}(?:0[48]|[2468][048]|[13579][26]))|(?:(?:[02468][048])|[13579][26])00)\-02\-29)$/.test(
              value
            )
          );
        })
        .test('is-date-future', 'Expiry date is in the past', (value) => {
          if (value === undefined) return true;
          if (value.charAt(4) === '-') return new Date(value) >= new Date();
          return new Date(value.split('-')[2], value.split('-')[1], value.split('-')[0]) >= new Date();
        }),
    }),
  }),
});

const FormatDate = (expiryDate) => {
  const date =
    expiryDate.charAt(4) === '-'
      ? new Date(expiryDate)
      : new Date(expiryDate.split('-')[2], expiryDate.split('-')[1], expiryDate.split('-')[0]);
  const dateTimeFormat = new Intl.DateTimeFormat('en', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const [{ value: month }, , { value: day }, , { value: year }] = dateTimeFormat.formatToParts(date);
  return `${year}-${month}-${day}T00:00:00.000Z`;
};

const ExemptionForm = ({ accountView, accountExemption }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [, putExemption, deleteExemption] = useExemption();
  const [exemptionUpdatedError, setExemptionUpdatedError] = useState('');
  const [isFeatureEnabled] = useFeatureFlagCheck();

  const exemptionReasons = exemptionList.filter((reason) => {
    const { feature_flag } = reason;
    const enabled = isFeatureEnabled(feature_flag);
    return !feature_flag || (feature_flag && enabled);
  });

  const handleRemoveExemption = async () => {
    const response = await deleteExemption();
    setExemptionUpdatedError(response);
  };

  const submitForm = async (payload, actions) => {
    if (payload.payForPrescriptions === 'Yes') {
      history.push('/pharmacy/dashboard/payment');
    } else {
      const selectedExemption = exemptionReasons.find((reason) => reason.option === payload.exemption);
      const exemptionFormResult = { type: selectedExemption };

      switch (selectedExemption?.validation?.type) {
        case 'ni':
          exemptionFormResult.ni_number = payload.ni_number;
          break;
        case 'cert_expiry':
          exemptionFormResult.cert_number = payload.cert.number;
          exemptionFormResult.cert_expiry = payload.cert.expiry ? FormatDate(payload.cert.expiry) : payload.cert.expiry;
          break;
        default:
          break;
      }

      dispatch({ type: ADD_EXEMPTION, payload: exemptionFormResult });
      history.push('/pharmacy/dashboard/basket');
    }
  };

  const updateExemption = async (payload, actions) => {
    const selectedExemption = exemptionReasons.find((reason) => reason.option === payload.exemption);
    const data = {
      selectedExemption,
      cert: {
        number: payload?.cert?.number,
        expiry: payload?.cert?.expiry ? FormatDate(payload?.cert?.expiry) : payload?.cert?.expiry,
      },
      ni_number: payload?.ni_number,
    };
    const response = await putExemption(data);
    setExemptionUpdatedError(response);
  };

  const payForPrescriptions = () => {
    let pfp;
    if (accountView) {
      pfp = 'No';
    } else {
      if (accountExemption.code) {
        pfp = 'No';
      } else {
        pfp = 'Yes';
      }
    }

    return pfp;
  };

  const exemptionToDisplay = () => {
    const { code } = accountExemption;
    if (accountView) {
      return code || '';
    } else {
      return code !== 'W' && code;
    }
  };

  const initialValues = {
    payForPrescriptions: payForPrescriptions(),
    exemption: exemptionToDisplay(),
    ni_number: '',
    cert: {
      number: (accountExemption?.code && accountExemption?.number) || '',
      expiry:
        accountExemption?.code && accountExemption?.expiry_date
          ? moment(accountExemption?.expiry_date).format('YYYY-MM-DD')
          : '',
    },
  };

  const requireForm = (payForPrescriptions, exemption, search) => {
    if (payForPrescriptions === 'Yes') return false;
    return !!exemptionReasons.find((reason) => reason.option === exemption && reason?.validation?.type === search);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={ExemptionFormSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values, actions) => {
          if (accountView) {
            updateExemption(values, actions);
          } else {
            submitForm(values, actions);
          }
        }}>
        {(props) => (
          <form className={styles.form} onSubmit={props.handleSubmit}>
            {!accountView && (
              <Field component="div" name="payForPrescriptions">
                <fieldset className={`${styles.choice} coop-form__choice `}>
                  <div
                    tabIndex="0"
                    onKeyPress={(event) => {
                      if (event.key === 'Enter') props.values.payForPrescriptions = 'Yes';
                    }}
                    className="coop-form__choice">
                    <input
                      type="radio"
                      name="payForPrescriptions"
                      checked={props.values.payForPrescriptions === 'Yes'}
                      id="option-1"
                      readOnly
                      value="Yes"
                      className="coop-form__field coop-form__radio"
                    />
                    <label className={styles.payLabelYes} htmlFor="option-1">
                      <div className={`${styles.box_container__grid}`}>
                        <div className={styles.image_container}>
                          <div
                            className={`${styles.dot} ${
                              props.values.payForPrescriptions === 'Yes' && styles.dot__active
                            }`}
                          />
                        </div>
                        <div className={styles.box_container__text}>Yes</div>
                      </div>
                    </label>
                  </div>
                  <div
                    tabIndex="0"
                    onKeyPress={(event) => {
                      if (event.key === 'Enter') props.values.payForPrescriptions = 'No';
                    }}
                    className="coop-form__choice">
                    <input
                      type="radio"
                      name="payForPrescriptions"
                      checked={props.values.payForPrescriptions === 'No'}
                      readOnly
                      id="option-2"
                      value="No"
                      className="coop-form__field coop-form__radio"
                    />
                    <label className={styles.payLabelNo} htmlFor="option-2">
                      <div className={`${styles.box_container__grid}`}>
                        <div className={styles.image_container}>
                          <div
                            className={`${styles.dot} ${
                              props.values.payForPrescriptions === 'No' && styles.dot__active
                            }`}
                          />
                        </div>
                        <div className={styles.box_container__text}>No</div>
                      </div>
                    </label>
                  </div>
                </fieldset>
              </Field>
            )}
            {props.values.payForPrescriptions === 'No' && (
              <div className={styles.choice}>
                <Field component="div" name="exemption">
                  <label className={styles.choiceLabel} htmlFor="exemption">
                    <h2>Exemption type</h2>
                    <select
                      className={styles.select}
                      id="exemption"
                      value={props.values.exemption}
                      onChange={(e) => {
                        const { value } = e.target;
                        const { code } = accountExemption;
                        if (value !== code) {
                          props.setFieldValue('cert.number', '');
                          props.setFieldValue('cert.expiry', '');
                          props.setFieldValue('ni_number', '');
                        } else {
                          props.setFieldValue('cert.number', initialValues?.cert?.number || '');
                          props.setFieldValue('cert.expiry', initialValues?.cert?.expiry || '');
                          props.setFieldValue('ni_number', '');
                        }
                        return props.handleChange;
                      }}>
                      <option value="">Select your exemption</option>
                      {exemptionReasons.map((exemption) => (
                        <option key={exemption.option} value={exemption.option}>
                          {exemption.option}. {exemption.text}
                        </option>
                      ))}
                    </select>
                  </label>
                  <ErrorMessage
                    name="exemption"
                    render={(msg) => <div className={`coop-form__error ${styles.errorText}`}>{msg}</div>}
                  />
                </Field>
                {requireForm(props.values.payForPrescriptions, props.values.exemption, 'cert_expiry') && (
                  <>
                    <label className={styles.choiceLabel} htmlFor="cert.number">
                      <h2>Certificate number</h2>
                      <input
                        className={styles.inputBox}
                        maxLength="255"
                        id="cert.number"
                        type="text"
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        value={props.values.cert.number}
                        name="cert.number"
                      />
                    </label>
                    <ErrorMessage
                      name="cert.number"
                      render={(msg) => <div className={`coop-form__error ${styles.errorText}`}>{msg}</div>}
                    />
                    <label className={styles.choiceLabel} htmlFor="cert.expiry">
                      <h2>Certificate expiry</h2>
                      <input
                        className={`${styles.expiryInput} ${styles.inputBox}`}
                        maxLength="255"
                        id="cert.cert_number"
                        type="date"
                        placeholder="dd-mm-yyyy"
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        value={props.values.cert.expiry}
                        name="cert.expiry"
                      />
                    </label>
                    <ErrorMessage
                      name="cert.expiry"
                      render={(msg) => <div className={`coop-form__error ${styles.errorText}`}>{msg}</div>}
                    />
                  </>
                )}
                {requireForm(props.values.payForPrescriptions, props.values.exemption, 'ni') && (
                  <>
                    <label className={styles.choiceLabel} htmlFor="ni_number">
                      <h2>National insurance number</h2>
                      <input
                        className={styles.inputBox}
                        maxLength="255"
                        id="ni_number"
                        type="text"
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        value={props.values.ni_number}
                        name="ni_number"
                      />
                    </label>
                    <ErrorMessage
                      name="ni_number"
                      render={(msg) => <div className={`coop-form__error ${styles.errorText}`}>{msg}</div>}
                    />
                  </>
                )}
              </div>
            )}
            {props.values.exemption === 'W' && (
              <a
                className={styles.linkText}
                href="https://www.nhsbsa.nhs.uk/help-nhs-prescription-costs/nhs-hormone-replacement-therapy-prescription-prepayment-certificate-hrt-ppc"
                target="_blank"
                rel="noreferrer">
                Buy a HRT PPC certificate
              </a>
            )}
            <button type="submit" className={`coop-btn coop-btn--primary coop-c-hero__cta ${styles.submit}`}>
              {accountView ? 'Update' : 'Next'}
            </button>
          </form>
        )}
      </Formik>
      {accountView && (
        <button
          className={`coop-btn coop-btn--primary coop-c-hero__cta ${styles.submitConditional}`}
          onClick={handleRemoveExemption}>
          Remove Exemption
        </button>
      )}
      {exemptionUpdatedError && (
        <div className={styles.exemptionUpdateStatus}>
          <div>{exemptionUpdatedError}</div>
        </div>
      )}
    </>
  );
};

export default ExemptionForm;
